import { supabase } from './supabase';
import { SUPABASE_ANON_KEY } from './constants';

// Identifier persisted between the public agent landing page and the
// onboarding/signup flow. Holds either the agent's slug or referral_code.
export const PENDING_AGENT_STORAGE_KEY = 'pending_referring_agent';

export type ReferringAgent = {
  id: string;
  fullName: string;
  brokerage: string | null;
  photoUrl: string | null;
  bio?: string | null;
  slug: string;
};

export type ValidateAgentResult =
  | { valid: true; agent: ReferringAgent }
  | { valid: false; error?: string };

export type LinkAgentResult =
  | { success: true; agent: ReferringAgent; alreadyLinked?: boolean }
  | { success: false; error: string };

export const setPendingAgentIdentifier = (identifier: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (!identifier) {
      window.sessionStorage.removeItem(PENDING_AGENT_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(PENDING_AGENT_STORAGE_KEY, identifier);
    }
  } catch (err) {
    console.warn('Could not access sessionStorage for pending agent:', err);
  }
};

export const getPendingAgentIdentifier = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(PENDING_AGENT_STORAGE_KEY);
  } catch {
    return null;
  }
};

// Looks up an agent by slug or referral_code. Anonymous - safe to call
// from the public landing page or onboarding flow before sign in.
export const validateAgentIdentifier = async (
  identifier: string
): Promise<ValidateAgentResult> => {
  const trimmed = identifier.trim();
  if (!trimmed) return { valid: false, error: 'Identifier is empty' };

  try {
    const response = await fetch(
      `${(supabase as any).supabaseUrl}/functions/v1/validate-agent-code`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY || ''}`,
          apikey: SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({ identifier: trimmed }),
      }
    );
    const result = await response.json();
    if (result?.valid && result?.agent) {
      return { valid: true, agent: result.agent as ReferringAgent };
    }
    return { valid: false, error: result?.error };
  } catch (err: any) {
    console.error('Error validating agent identifier:', err);
    return { valid: false, error: err?.message || 'Network error' };
  }
};

// Persists the agent link on the authenticated homeowner's profile and
// writes an audit row. Requires an active session.
export const linkReferringAgent = async (
  identifier: string,
  source: 'signup' | 'self_linked' = 'self_linked'
): Promise<LinkAgentResult> => {
  const trimmed = identifier.trim();
  if (!trimmed) return { success: false, error: 'Identifier is empty' };

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return { success: false, error: 'Not signed in' };
    }

    const response = await fetch(
      `${(supabase as any).supabaseUrl}/functions/v1/link-referring-agent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ identifier: trimmed, source }),
      }
    );
    const result = await response.json();
    if (response.ok && result?.success && result?.agent) {
      return {
        success: true,
        agent: result.agent as ReferringAgent,
        alreadyLinked: !!result.alreadyLinked,
      };
    }
    return {
      success: false,
      error: result?.error || 'Failed to link agent',
    };
  } catch (err: any) {
    console.error('Error linking referring agent:', err);
    return { success: false, error: err?.message || 'Network error' };
  }
};
